import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Select, InputNumber, message, Card, Typography, Popconfirm, Tag } from 'antd';
import { LinkOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

interface Printer {
  id: number;
  name: string;
  model: string;
}

interface Material {
  id: number;
  name: string;
  color: string;
}

interface Association {
  id: number;
  printer_id: number;
  material_id: number;
  remaining_weight: number;
  is_current: boolean;
  installed_at: string;
  printer?: Printer;
  material?: Material;
}

const AssociationManagement: React.FC = () => {
  const [associations, setAssociations] = useState<Association[]>([]);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAssociation, setEditingAssociation] = useState<Association | null>(null);
  const [selectedPrinterId, setSelectedPrinterId] = useState<number | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPrinters();
    fetchMaterials();
  }, []);

  useEffect(() => {
    if (selectedPrinterId) {
      fetchAssociations(selectedPrinterId);
    } else {
      setAssociations([]);
    }
  }, [selectedPrinterId]);

  const fetchPrinters = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/printers', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrinters(data);
      }
    } catch (error) {
      console.error('获取打印机列表失败:', error);
      message.error('获取打印机列表失败');
    }
  };

  const fetchMaterials = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/materials', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setMaterials(data);
      }
    } catch (error) {
      console.error('获取材料列表失败:', error);
      message.error('获取材料列表失败');
    }
  };

  const fetchAssociations = async (printerId: number) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/printers/${printerId}/materials`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setAssociations(data);
      }
    } catch (error) {
      console.error('获取关联列表失败:', error);
      message.error('获取关联列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (!selectedPrinterId) {
      message.warning('请先选择打印机');
      return;
    }
    setEditingAssociation(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (association: Association) => {
    setEditingAssociation(association);
    form.setFieldsValue({
      material_id: association.material_id,
      remaining_weight: association.remaining_weight,
      is_current: association.is_current,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    if (!selectedPrinterId) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/printers/${selectedPrinterId}/materials/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        message.success('删除成功');
        fetchAssociations(selectedPrinterId);
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || '删除失败');
      }
    } catch (error) {
      console.error('删除关联失败:', error);
      message.error('删除失败');
    }
  };

  const handleSetCurrent = async (id: number) => {
    if (!selectedPrinterId) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/printers/${selectedPrinterId}/materials/${id}/set-current`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        message.success('已设置为当前耗材');
        fetchAssociations(selectedPrinterId);
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || '设置失败');
      }
    } catch (error) {
      console.error('设置当前耗材失败:', error);
      message.error('设置失败');
    }
  };

  const handleSubmit = async () => {
    if (!selectedPrinterId) return;
    
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('admin_token');
      
      let response;
      if (editingAssociation) {
        // 更新关联
        response = await fetch(`/api/admin/printers/${selectedPrinterId}/materials/${editingAssociation.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });
      } else {
        // 创建关联
        response = await fetch(`/api/admin/printers/${selectedPrinterId}/materials`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });
      }
      
      if (response.ok) {
        message.success(editingAssociation ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchAssociations(selectedPrinterId);
      } else {
        const errorData = await response.json();
        message.error(errorData.detail || '操作失败');
      }
    } catch (error) {
      console.error('保存关联失败:', error);
      message.error('保存失败');
    }
  };

  const columns = [
    {
      title: '材料名称',
      key: 'material_name',
      render: (_: any, record: Association) => (
        <Space>
          <div style={{
            width: '16px',
            height: '16px',
            backgroundColor: record.material?.color || '#1890ff',
            borderRadius: '2px',
            border: '1px solid #d9d9d9',
          }} />
          <Text strong>{record.material?.name || '未知材料'}</Text>
        </Space>
      ),
    },
    {
      title: '剩余重量',
      dataIndex: 'remaining_weight',
      key: 'remaining_weight',
      render: (weight: number) => `${weight}g`,
    },
    {
      title: '状态',
      dataIndex: 'is_current',
      key: 'is_current',
      render: (isCurrent: boolean) => (
        isCurrent ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            当前耗材
          </Tag>
        ) : (
          <Tag color="default">备用耗材</Tag>
        )
      ),
    },
    {
      title: '安装时间',
      dataIndex: 'installed_at',
      key: 'installed_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Association) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {!record.is_current && (
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => handleSetCurrent(record.id)}
            >
              设为当前
            </Button>
          )}
          <Popconfirm
            title="确定要删除这个关联吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={4}>关联管理</Title>
          <Space>
            <Select
              placeholder="选择打印机"
              style={{ width: 200 }}
              onChange={(value) => setSelectedPrinterId(value)}
              allowClear
            >
              {printers.map(printer => (
                <Option key={printer.id} value={printer.id}>
                  {printer.name} ({printer.model})
                </Option>
              ))}
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={!selectedPrinterId}
            >
              添加关联
            </Button>
          </Space>
        </div>
        
        {selectedPrinterId ? (
          <Table
            columns={columns}
            dataSource={associations}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <LinkOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
            <Text type="secondary">请先选择打印机以查看和管理材料关联</Text>
          </div>
        )}
      </Card>

      <Modal
        title={editingAssociation ? '编辑关联' : '添加关联'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ 
            remaining_weight: 1000,
            is_current: false
          }}
        >
          <Form.Item
            name="material_id"
            label="选择材料"
            rules={[{ required: true, message: '请选择材料' }]}
          >
            <Select placeholder="请选择材料">
              {materials.map(material => (
                <Option key={material.id} value={material.id}>
                  <Space>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      backgroundColor: material.color || '#1890ff',
                      borderRadius: '2px',
                      border: '1px solid #d9d9d9',
                    }} />
                    {material.name}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="remaining_weight"
            label="剩余重量"
            rules={[{ required: true, message: '请输入剩余重量' }]}
          >
            <InputNumber
              min={0}
              max={10000}
              step={10}
              style={{ width: '100%' }}
              placeholder="例如：1000"
              addonAfter="g"
            />
          </Form.Item>
          
          <Form.Item
            name="is_current"
            label="是否设为当前耗材"
            valuePropName="checked"
          >
            <Select>
              <Option value={true}>是（设为当前使用的耗材）</Option>
              <Option value={false}>否（设为备用耗材）</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssociationManagement;
